import { Button, Drawer, Hairline, Input, Label, Tabs } from "@components";
import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import styles from "../../../styles/pages/cdp/cdp.module.scss";
import classNames from "classnames/bind";
import { InputSearch } from "shared/components/InputSearch";
import { CheckEdge } from "shared/components/Icons";
import OcdpAPI2 from "providers/OcdpAPI2";
const css = classNames.bind(styles);

const tabs = {
  CUSTOMER: {
    key: "CUSTOMER",
    name: "Clientes",
  },
  ORDER: {
    key: "ORDER",
    name: "Pedidos",
  },
};

const colors = [
  "#733BD4",
  "#FF66B7",
  "#A800B1",
  "#C24026",
  "#E5905F",
  "#E5B33F",
  "#4667EE",
  "#00F2FF",
  "#1DC45D",
  "#F4F4F4",
];

const ocdp_api = new OcdpAPI2();

const BaseMarking = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(tabs.CUSTOMER.key);
  const [marking, setMarking] = useState("");
  const [color, setColor] = useState(null);
  const [markups, setMarkups] = useState([]);
  const [removeMarkup, setRemoveMarkup] = useState(null);
  const [timer, setTimer] = useState(5);
  const [markupRemoved, setMarkupRemoved] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchMarkups = async () => {
      const markups = await ocdp_api.getMarkups();
      setMarkups(markups);
    };

    fetchMarkups();
  }, []);

  useEffect(() => {
    let interval;

    if (removeMarkup) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [removeMarkup]);

  useEffect(() => {
    if (open) {
      setActiveTab(tabs.CUSTOMER.key);
      setMarking(null);
      setColor(null);
      setRemoveMarkup(null);
      setTimer(5);
      setMarkupRemoved(false);
    }
  }, [open]);

  const filteredMarkups = markups.filter((markup) => {
    if (markup.type !== activeTab) return false;

    const searchLower = search.toLowerCase();
    return markup.name.toLowerCase().includes(searchLower);
  });

  const deleteMarkup = async (id) => {
    const response = await ocdp_api.deleteMarkup(id);
    if (response.getErrors().length > 0)
      alert({ text: response.getErrors()[0], buttonText: "Ok" });
    else setMarkups(markups.filter((m) => m.id !== id));
  };

  const saveMarkup = async () => {
    const response = await ocdp_api.postMarkups({
      name: marking,
      color,
      type: activeTab,
    });

    if (response?.getErrors([])?.length)
      alert({ text: response.getErrors([])[0], buttonText: "Ok" });
    else {
      setMarking("");
      setColor(null);
      setMarkups((prev) => [
        ...prev,
        {
          name: marking,
          icon: null,
          color,
          id: (markups.length || 0) + 1,
          type: activeTab,
        },
      ]);
    }
  };

  return (
    <Drawer
      visible={open}
      onClose={() => onClose()}
      title={removeMarkup ? "Remover marcação" : "Marcação de base"}
      subtitle={markupRemoved ? "Alterações feitas com sucesso." : null}
      withoutTransition
      showBackButton
      backButton={() => {
        if (removeMarkup) {
          setMarkupRemoved(false);
          setRemoveMarkup(null);
        } else onClose();
      }}
      footer={
        removeMarkup && !markupRemoved ? (
          <Button
            design="primary"
            className="w-100"
            onClick={() => {
              setMarkupRemoved(true);
              deleteMarkup(removeMarkup.id);
              setMarkups(markups.filter((m) => m.id !== removeMarkup.id));
            }}
            disabled={timer > 0}
          >
            Remover marcação {timer > 0 && `(${timer}s)`}
          </Button>
        ) : markupRemoved ? (
          <Button
            className="w-100"
            onClick={() => {
              setMarkupRemoved(false);
              setRemoveMarkup(null);
            }}
          >
            Fechar
          </Button>
        ) : (
          <Button design="primary" className="w-100" onClick={onClose}>
            Salvar
          </Button>
        )
      }
    >
      <If cond={markupRemoved}>
        <div className="d-flex align-center justify-center flex-1">
          <CheckEdge />
        </div>
      </If>
      <If cond={removeMarkup && !markupRemoved}>
        <div className={styles.removeMarkup}>
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
              fill="#D9D26C"
              fillOpacity="0.05"
            />
            <path
              d="M21.9996 16.3996C23.4848 16.3996 24.9092 16.9896 25.9594 18.0398C27.0096 19.09 27.5996 20.5144 27.5996 21.9996C27.5996 23.4848 27.0096 24.9092 25.9594 25.9594C24.9092 27.0096 23.4848 27.5996 21.9996 27.5996C20.5144 27.5996 19.09 27.0096 18.0398 25.9594C16.9896 24.9092 16.3996 23.4848 16.3996 21.9996C16.3996 20.5144 16.9896 19.09 18.0398 18.0398C19.09 16.9896 20.5144 16.3996 21.9996 16.3996ZM21.9996 28.3996C23.697 28.3996 25.3249 27.7253 26.5251 26.5251C27.7253 25.3249 28.3996 23.697 28.3996 21.9996C28.3996 20.3022 27.7253 18.6744 26.5251 17.4741C25.3249 16.2739 23.697 15.5996 21.9996 15.5996C20.3022 15.5996 18.6744 16.2739 17.4741 17.4741C16.2739 18.6744 15.5996 20.3022 15.5996 21.9996C15.5996 23.697 16.2739 25.3249 17.4741 26.5251C18.6744 27.7253 20.3022 28.3996 21.9996 28.3996ZM21.9996 18.7996C21.7796 18.7996 21.5996 18.9796 21.5996 19.1996V22.3996C21.5996 22.6196 21.7796 22.7996 21.9996 22.7996C22.2196 22.7996 22.3996 22.6196 22.3996 22.3996V19.1996C22.3996 18.9796 22.2196 18.7996 21.9996 18.7996ZM22.5996 24.3996C22.5996 24.2405 22.5364 24.0879 22.4239 23.9753C22.3114 23.8628 22.1587 23.7996 21.9996 23.7996C21.8405 23.7996 21.6879 23.8628 21.5753 23.9753C21.4628 24.0879 21.3996 24.2405 21.3996 24.3996C21.3996 24.5587 21.4628 24.7114 21.5753 24.8239C21.6879 24.9364 21.8405 24.9996 21.9996 24.9996C22.1587 24.9996 22.3114 24.9364 22.4239 24.8239C22.5364 24.7114 22.5996 24.5587 22.5996 24.3996Z"
              fill="#FFC741"
            />
          </svg>

          <p className={styles.title}>Atenção</p>
          <span>
            Ao remover esta marcação, ela será <strong>excluída</strong> de
            todos os {activeTab === tabs.CUSTOMER.key ? "clientes" : "pedidos"}{" "}
            que estão marcados. Deseja continuar?
          </span>

          <Hairline className="mt-15 mb-15" />

          <div className={styles.markup}>
            <div className="d-flex align-center h-100">
              <div
                className={styles.color}
                style={{ backgroundColor: removeMarkup?.color }}
              />
              <div className={styles.tag}>
                <i className="fal fa-tags" />
              </div>
              <p>{removeMarkup?.name}</p>
            </div>
          </div>
        </div>
      </If>

      <If cond={!removeMarkup}>
        <Tabs
          full
          tabs={Object.values(tabs)}
          onTabSelected={(tab) => setActiveTab(tab)}
          activeTab={activeTab}
        />

        <div className={styles.markupsScroll}>
          <div className={styles.box}>
            <Label>Adicione uma marcação</Label>
            <Input
              value={marking}
              onChange={(e) => setMarking(e.target.value)}
            />

            <div className={styles.chooseColor}>
              <Label required>Selecione uma cor</Label>
              <div className={styles.colors}>
                {colors.map((c) => {
                  return (
                    <div
                      className={css({ color: true, selected: color === c })}
                      style={{ backgroundColor: c }}
                      onClick={() => setColor(c)}
                    />
                  );
                })}
              </div>
            </div>
            <Button
              className="mt-15 w-100"
              disabled={!marking || !color}
              onClick={saveMarkup}
            >
              Adicionar
            </Button>
          </div>

          <Hairline className="mt-20 mb-20" />

          <Label>
            Marcações ({markups.filter((m) => m.type === activeTab).length})
          </Label>
          <InputSearch
            design="two"
            value={search}
            placeholder="Buscar por..."
            onChange={(e) => setSearch(e.target.value)}
          />
          <If cond={filteredMarkups.length > 0}>
            <div className={styles.markups}>
              {filteredMarkups.map((m) => {
                return (
                  <div
                    className={styles.markup}
                    style={{
                      backgroundColor: `rgb(from ${m?.color} r g b / 3%)`,
                    }}
                  >
                    <div className="d-flex align-center h-100">
                      <div
                        className={styles.color}
                        style={{ backgroundColor: m.color }}
                      />
                      <div className={styles.tag}>
                        <i className="fal fa-tags" />
                      </div>
                      <p>{m.name}</p>
                    </div>

                    <div
                      className={styles.erase}
                      onClick={() => setRemoveMarkup(m)}
                    >
                      <i className="far fa-times" />
                    </div>
                  </div>
                );
              })}
            </div>
          </If>
          <If cond={markups.filter((m) => m.type === activeTab).length === 0}>
            <div className={styles.emptyMarkups}>
              <i className="fal fa-tags" />
              <p>Nenhuma marcação adicionada</p>
              <If cond={activeTab === tabs.CUSTOMER.key}>
                <span>
                  Utilize esta ferramenta para categorizar seus clientes.
                </span>
              </If>
              <If cond={activeTab === tabs.ORDER.key}>
                <span>
                  Utilize esta ferramenta para categorizar os pedidos dos seus
                  clientes.
                </span>
              </If>
            </div>
          </If>
        </div>
      </If>
    </Drawer>
  );
};

export default BaseMarking;
